import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { CategoricalTileGrid } from "../tile_grid"
import { booleanDataSocket, categoricalDataSocket } from "../socket_types"
import { BaseComponent } from "./base_component"
import { getCategoricalLabelsFromInputChain } from "./categorical_label_chain"
import {
  isCategoricalChainSeedingSuppressed,
  isLabelOutputReconnectInProgressForNode,
  mergeLabelOutputWireSnapshotsForReconnect,
  type LabelOutputWireSnapshot,
  reconnectLabelOutputsByNormalizedLabel,
  rememberLabelOutputWireSnapshots,
  runWithLabelOutputReconnect,
  snapshotLabelOutputConnections,
  takePendingLabelOutputWireSnapshots,
} from "./categorical_dynamic_io_connections"

type LabelsByValue = Map<number, string>
type ValueByLabel = Map<string, number>

export class CategoricalMergeComponent extends BaseComponent {
  private static connectionHooksByNodeId = new Set<number>()

  constructor() {
    super("Categorical merge")
    this.category = "Set operations"
    this.contextMenuName = "Categorical merge"
  }

  async builder(node: Node) {
    node.addInput(new Input("base", "Base", categoricalDataSocket))
    node.addInput(new Input("updates", "Updates", categoricalDataSocket))
    node.addOutput(new Output("All", "All", categoricalDataSocket))
    const savedLabels = node.data.mergedLabels as Array<{ value: number; label: string }> | undefined
    const hasSavedLabels = Array.isArray(savedLabels) && savedLabels.length > 0
    if (hasSavedLabels) {
      this.syncBooleanOutputs(node, savedLabels!.map(({ value, label }) => [value, label]))
    } else {
      // No saved label set — derive one from the upstream chain. Skipped
      // when saved labels already provided the outputs (JSON replay will
      // restore matching connections by key).
      this.seedOutputsFromConnections(node)
    }

    // Auto-refresh outputs when connections change.
    // This makes label outputs appear/update as soon as Base/Updates are wired.
    if (this.editor && !CategoricalMergeComponent.connectionHooksByNodeId.has(node.id)) {
      CategoricalMergeComponent.connectionHooksByNodeId.add(node.id)
      const refreshOnConnectionChange = () => {
        // Skip during `editor.fromJSON` — chain walks (one per input × per
        // connection event × per merge node) dominate load time otherwise.
        if (isCategoricalChainSeedingSuppressed()) return
        this.seedOutputsFromConnections(node)
      }

      this.editor.on("connectioncreated", refreshOnConnectionChange)
      this.editor.on("connectionremoved", refreshOnConnectionChange)
      this.editor.on("noderemoved", (removedNode: any) => {
        if (removedNode?.id === node.id) {
          CategoricalMergeComponent.connectionHooksByNodeId.delete(node.id)
        }
      })
    }
  }

  private syncBooleanOutputs(node: Node, labels: Array<[number, string]>, snapsOverride?: LabelOutputWireSnapshot[]) {
    const snaps =
      snapsOverride !== undefined
        ? snapsOverride
        : this.editor != null
          ? snapshotLabelOutputConnections(node, k => k.startsWith("label_"))
          : []

    const getExistingOutput = (key: string) => {
      const outputsAny = node.outputs as any
      if (typeof outputsAny?.get === "function") return outputsAny.get(key)
      return outputsAny?.[key]
    }

    const desired = new Map<string, string>()
    labels
      .filter(([value]) => value !== 255)
      .sort((a, b) => a[0] - b[0])
      .forEach(([value, label]) => {
        desired.set(`label_${value}`, label)
      })

    Array.from(node.outputs.values()).forEach((output: any) => {
      if (!String(output.key).startsWith("label_")) return
      if (desired.has(output.key)) return
      ;[...output.connections].forEach(c => this.editor?.removeConnection(c))
      node.removeOutput(output)
    })

    desired.forEach((label, key) => {
      const existing = getExistingOutput(key)
      if (existing) {
        existing.name = label
      } else {
        node.addOutput(new Output(key, label, booleanDataSocket))
      }
    })

    if (this.editor) {
      runWithLabelOutputReconnect(node, () =>
        reconnectLabelOutputsByNormalizedLabel(this.editor!, node, snaps, k => k.startsWith("label_"))
      )
    }
  }

  private pruneOutputsWithoutLabels(node: Node) {
    if (this.editor) {
      rememberLabelOutputWireSnapshots(
        node,
        snapshotLabelOutputConnections(node, k => k.startsWith("label_"))
      )
    }
    Array.from(node.outputs.values()).forEach((output: any) => {
      if (!String(output.key).startsWith("label_")) return
      ;[...output.connections].forEach(c => this.editor?.removeConnection(c))
      node.removeOutput(output)
    })
  }

  private seedOutputsFromConnections(node: Node) {
    if (!this.editor) return
    if (isLabelOutputReconnectInProgressForNode(node.id)) return
    const normalizedSeen = new Set<string>()
    const orderedLabels: string[] = []
    const normalize = (label: string) => label.trim().toLowerCase()

    const pushLabel = (label: string) => {
      const trimmed = label.trim()
      if (!trimmed) return
      const key = normalize(trimmed)
      if (normalizedSeen.has(key)) return
      normalizedSeen.add(key)
      orderedLabels.push(trimmed)
    }

    const baseRows = getCategoricalLabelsFromInputChain(this.editor, node, "base")
    const updatesRows = getCategoricalLabelsFromInputChain(this.editor, node, "updates")
    baseRows.forEach(({ label }) => pushLabel(label))
    updatesRows.forEach(({ label }) => pushLabel(label))

    if (orderedLabels.length === 0) {
      // During `editor.fromJSON`, all node builders run before connections are applied.
      // Until Base/Updates are wired, the upstream chain is empty — do not wipe
      // `mergedLabels` or per-label outputs restored from JSON (downstream wires would fail to attach).
      const inputHasWire = (key: string) => {
        const inp = (node.inputs as any).get?.(key) ?? (node.inputs as any)[key]
        return Array.isArray(inp?.connections) && inp.connections.length > 0
      }
      if (!inputHasWire("base") && !inputHasWire("updates")) {
        node.update()
        return
      }
      delete node.data.mergedLabels
      this.pruneOutputsWithoutLabels(node)
      node.update()
      return
    }
    const labelEntries: Array<[number, string]> = orderedLabels.map((label, index) => [index + 1, label])
    node.data.mergedLabels = labelEntries.map(([value, label]) => ({ value, label }))
    const pending = takePendingLabelOutputWireSnapshots(node)
    const live =
      this.editor != null ? snapshotLabelOutputConnections(node, k => k.startsWith("label_")) : []
    const merged = mergeLabelOutputWireSnapshotsForReconnect(live, pending)
    this.syncBooleanOutputs(node, labelEntries, merged)
    node.update()
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (editorNode === undefined) return

    const baseInput = inputs["base"]?.[0]
    const updatesInput = inputs["updates"]?.[0]
    if (!(baseInput instanceof CategoricalTileGrid) || !(updatesInput instanceof CategoricalTileGrid)) {
      editorNode.meta.errorMessage = "Requires Base and Updates categorical inputs"
      return
    }
    delete editorNode.meta.errorMessage

    const outputLabels: LabelsByValue = new Map()
    const outputValueByLabel: ValueByLabel = new Map()

    const normalizeLabel = (label: string) => label.trim().toLowerCase()
    const sortedBaseLabels = Array.from(baseInput.labels?.entries() ?? []).sort((a, b) => a[0] - b[0])
    sortedBaseLabels.forEach(([value, label]) => {
      if (value === 255) return
      outputLabels.set(value, label)
      outputValueByLabel.set(normalizeLabel(label), value)
    })
    let nextLabelValue = sortedBaseLabels.length > 0
      ? Math.max(...sortedBaseLabels.map(([value]) => value)) + 1
      : 1

    const ensureOutputValueForLabel = (label: string): number => {
      const normalized = normalizeLabel(label)
      const existing = outputValueByLabel.get(normalized)
      if (existing != null) return existing
      const assigned = nextLabelValue
      nextLabelValue += 1
      outputValueByLabel.set(normalized, assigned)
      outputLabels.set(assigned, label)
      return assigned
    }
    const valueToOutputValue = (value: number, labels?: LabelsByValue): number => {
      if (value === 255) return 255
      const label = labels?.get(value)
      const resolvedLabel = (label && label.trim().length > 0) ? label : `Category ${value}`
      return ensureOutputValueForLabel(resolvedLabel)
    }

    const output = new CategoricalTileGrid(baseInput.zoom, baseInput.x, baseInput.y, baseInput.width, baseInput.height)

    // Initialize from base (mapped by label name, not index).
    baseInput.iterate((x, y, value) => {
      output.set(x, y, valueToOutputValue(value, baseInput.labels))
    })

    // Apply updates on top (overwrite non-empty update cells).
    updatesInput.iterate((x, y, updateValue) => {
      if (updateValue === 255) return
      const mappedUpdateValue = valueToOutputValue(updateValue, updatesInput.labels)
      output.set(x, y, mappedUpdateValue)
    })

    output.setLabels(outputLabels)
    output.name = (editorNode.data.name as string) || "Categorical merge"
    outputs["All"] = output

    const labelEntries = Array.from(outputLabels.entries()).sort((a, b) => a[0] - b[0])
    editorNode.data.mergedLabels = labelEntries.map(([value, label]) => ({ value, label }))
    this.syncBooleanOutputs(editorNode, labelEntries)

    labelEntries.forEach(([value, label]) => {
      const outputKey = `label_${value}`
      const labelOutput = (editorNode.outputs as any).get?.(outputKey) ?? (editorNode.outputs as any)[outputKey]
      if (!labelOutput) return
      const boolGrid = output.getBoolFromLabel(label)
      boolGrid.name = label
      outputs[outputKey] = boolGrid
    })

    editorNode.update()
  }
}

